import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, CheckCircle2, PhoneCall, RotateCcw } from "lucide-react";
import Logo from "@/components/Logo";
import { getReport } from "@/lib/api";

const BAND_THEME = {
  green: { hex: "#22C55E", text: "#15803D", bg: "#22C55E14", cta: { label: "Reassess anytime", to: "/assessment" } },
  yellow: { hex: "#EAB308", text: "#A16207", bg: "#EAB30814", cta: { label: "Book Basic Evaluation", to: "/book?tier=basic" } },
  orange: { hex: "#F97316", text: "#C2410C", bg: "#F9731614", cta: { label: "Book Advance Evaluation", to: "/book?tier=advanced" } },
  red: { hex: "#EF4444", text: "#B91C1C", bg: "#EF444412", cta: { label: "Request Priority Consultation", to: "/book?priority=1" } },
};

// Minimal secure report view — target of the WhatsApp "View my report" CTA.
// Read-only: shows the band, guidance and next step. No contact data, no raw
// responses, no score details beyond what the patient saw post-assessment.
export default function Report() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getReport(id).then(setReport).catch(() => setError("not-found"));
  }, [id]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-sa-bg px-6 text-center" data-testid="report-not-found">
        <Logo iconClass="h-10 w-10" textClass="text-xl" />
        <p className="mt-8 font-display text-2xl text-sa-forest">This report link is not valid.</p>
        <p className="mt-2 text-sm text-sa-muted">If you completed an assessment, your report opens right after submission.</p>
        <Link to="/assessment" className="mt-6 rounded-full bg-sa-forest px-6 py-3 text-sm font-medium text-white" data-testid="report-restart">
          Take the free assessment
        </Link>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sa-bg">
        <Loader2 className="h-6 w-6 animate-spin text-sa-copper" />
      </div>
    );
  }

  const theme = BAND_THEME[report.band] || BAND_THEME.green;
  const plan = report.recovery_plan;

  return (
    <div className="min-h-screen bg-sa-bg" data-testid="report-page">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between px-6 py-5">
        <Link to="/" data-testid="report-logo"><Logo iconClass="h-9 w-9" textClass="text-xl" /></Link>
        <span className="text-[12px] font-medium uppercase tracking-[0.16em] text-sa-muted">Liver Health Report</span>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 pb-16 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-[28px] border border-sa-beige bg-white p-7 shadow-[0_24px_60px_-24px_rgba(10,46,34,0.18)] sm:p-9"
        >
          {report.first_name && (
            <p className="text-sm text-sa-muted">Hi {report.first_name},</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-medium text-sa-forest">Your assessment result</h1>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: theme.bg, color: theme.text }}
              data-testid="report-band"
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: theme.hex }} />
              {report.label || report.band}
            </span>
          </div>
          {report.created_at && (
            <p className="mt-1.5 text-[12px] text-sa-muted">
              {new Date(report.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}

          {report.headline && (
            <p className="mt-6 font-display text-xl font-medium leading-snug text-sa-forest" data-testid="report-headline">
              {report.headline}
            </p>
          )}
          {report.summary && (
            <p className="mt-3 text-[15px] leading-relaxed text-sa-muted" data-testid="report-summary">{report.summary}</p>
          )}

          {report.recommendations?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-sa-beige bg-sa-bg/60 p-5">
              <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-sa-copper">What this means for you</p>
              <ul className="mt-3 space-y-2.5">
                {report.recommendations.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] leading-relaxed text-sa-forest" data-testid={`report-rec-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sa-copper" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {plan?.sections?.length > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {plan.sections.filter((s) => s.items?.length).map((s) => (
                <div key={s.key || s.title} className="rounded-2xl border border-sa-beige p-5" data-testid={`report-plan-${s.key || s.title}`}>
                  <p className="text-[13px] font-semibold text-sa-forest">{s.title}</p>
                  <ul className="mt-2 space-y-1.5">
                    {s.items.slice(0, 3).map((it, i) => (
                      <li key={i} className="text-[12.5px] leading-relaxed text-sa-muted">· {it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {report.next_step && (
            <p className="mt-6 rounded-2xl px-5 py-4 text-[14px] font-medium leading-relaxed" style={{ backgroundColor: theme.bg, color: theme.text }} data-testid="report-next-step">
              {report.next_step}
            </p>
          )}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to={theme.cta.to}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-sa-forest px-6 py-3.5 text-[15px] font-medium text-white transition-transform hover:scale-[1.01]"
              data-testid="report-cta"
            >
              {report.band === "red" ? <PhoneCall className="h-4 w-4" /> : report.band === "green" ? <RotateCcw className="h-4 w-4" /> : null}
              {theme.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        <p className="mt-6 text-center text-[12px] leading-relaxed text-sa-muted">
          This is a screening summary, not a medical diagnosis. Your full clinical details stay private on SuperAge Health.
        </p>
      </main>
    </div>
  );
}
